import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import styles from './Checkbox.module.scss';
import { Icon } from '../../icons/Icon';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Whether the checkbox is in an indeterminate state
   */
  indeterminate?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Error state
   */
  error?: boolean;

  /**
   * Whether the checkbox is required
   */
  testId?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      indeterminate = false,
      className,
      error = false,
      disabled = false,
      testId,
      ...props
    },
    ref
  ) => {
    const checkboxId = React.useId();
    const isDisabled = disabled;

    const checkboxClasses = clsx(
      styles.root,
      {
        [styles.disabled]: isDisabled,
        [styles.error]: error,
        [styles.indeterminate]: indeterminate,
      },
      className
    );

    const inputClasses = clsx(styles.input);

    const checkboxBoxClasses = clsx(styles.checkboxBox, {
      [styles.disabled]: isDisabled,
      [styles.error]: error,
      [styles.checked]: props.checked,
      [styles.indeterminate]: indeterminate,
    });

    return (
      <label className={checkboxClasses} htmlFor={checkboxId}>
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          data-testid={testId}
          className={inputClasses}
          disabled={isDisabled}
          aria-invalid={error}
          {...props}
        />
        <div className={checkboxBoxClasses}>
          {props.checked && !indeterminate && (
            <Icon name="check" size={12} className={styles.checkIcon} />
          )}
          {indeterminate && (
            <Icon name="minus" size={12} className={styles.indeterminateIcon} />
          )}
        </div>
      </label>
    );
  }
);
